import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import {
  BaseBox,
  FlexBox,
  Icons,
  SecondaryButton,
  Heading4,
  PrimaryButton,
  CenterBox,
  message,
  DoconModal,
  DoconModalContent,
} from '@docon/docon-ui-library';
import { Image } from 'shared';
import queryString from 'query-string';
import { WhatsAppIcon } from 'shared/icons';
import { bgColor, colors } from 'utilities/colorConfig';
import { CircleWithTick, CloseIcon, HelpIcon } from '@docon/docon-ui-library/lib/V2/shared/icons';
import GatewayPeLab from 'components/Records/GatewayPeLab';
import { cleverTapAnl } from 'utilities';
import { EventPEOrderingService, EventPEOrderingServiceView } from 'utilities/clevertapEvent';
import { isMobileOnly } from 'react-device-detect';

const certifiedTick = '/images/certifiedTick.svg';
const copyCodeImg = '/images/copyCode.svg';

const Modal = styled(DoconModal)`
  width: 95%;
  max-width: 666px;
  height: 100%;
  max-height: 80%;
  border-radius: 8px;
  overflow: hidden;
  position: relative;

  ${DoconModalContent} {
    max-height: initial;
    padding: 0;
    overflow: hidden;
  }

  ${() => isMobileOnly && css`
    max-height: 95%;
  `}
`;

const BackgroundImage = styled(Image)`
  width: 100%;
  height: 230px; 
`;

const ImageBox = styled(FlexBox)`
  height: 230px;
`;

const Root = styled(BaseBox)`
  position: relative;

  ${ImageBox},${BackgroundImage}{
    ${() => isMobileOnly && css` 
    @media screen and (min-width: 700px) {
      height: 300px;  
    }
    @media screen and (max-width: 500px) {
      height: 200px;  
    }
    
    @media screen and (max-width: 235px) {
      height: 300px;  
    }
    `};
  }
`;

const MedicineRow = styled(FlexBox)`
  padding: 12px 0px;    
  border-bottom: 1px solid ${colors.whitesmokeColor};
  gap: 0.5rem;
  flex-wrap: nowrap;

  ${CircleWithTick}{
    color:${colors.greenColor[2]};
  }
  ${CloseIcon}{
    color:${colors.redColor[2]};
  }
`;

const LabRow = styled(FlexBox)`
  padding-top: 12px;
  display: inline-flex;
`;

const HelpBox = styled(BaseBox)`
   flex: 1;
   ${HelpIcon}{
     color: ${colors.primaryBlue};
     cursor: pointer;
     float: right;
   }
`;

const Footer = styled(FlexBox)`
  padding: 12px 0px;
  justify-content: space-around;
  
  ${PrimaryButton}{
    ${() => isMobileOnly && css` 
      @media screen and (max-width: 375px) {
        width: 130px;
        padding: 0;
      }
      @media screen and (max-width: 353px) {
        width: 120px;
      }
      @media screen and (max-width: 342px) {
        width: 106px;
        font-size: 13px;
      }
      @media screen and (max-width: 327px) {
        width: 150px;
        font-size: 14px;
      }
    `};
  }
`;

const Box = styled(CenterBox)`
  margin-top: 10px;
  background-color: ${bgColor.base};
  border-radius: 4px;
  
  ${Heading4}{
    color: ${colors.secondaryColor};
    text-align: center;
    font-size: 14px;
  }
`;

const CopyBox = styled(FlexBox)`
  border: 1px dashed ${colors.purpleColor[4]};
  border-radius: 4px;
  justify-content: space-between;

  ${() => !isMobileOnly && css` 
    min-width: 150px;
    @media screen and (min-width: 560px) {
      width: 50%
    }
  `};

  ${() => isMobileOnly && css` 
    @media screen and (min-width: 415px) {
      width: 50%
    }
    @media screen and (max-width: 327px) {
      width: 150px;
    }
  `};

  ${Image}{
    width: 20px;
    margin-right: 8px;
    cursor: pointer;
  }
`;

const Bullet = styled(BaseBox)`
  background-color: ${colors.primaryColor};
  border-radius: 100%;
  width: 8px;
  height: 8px;
  margin: 8px;
`;

function LabCart({
  visitID,
  patientID,
  doctorID,
  searchInput,
  pincodeUpdated,
  state,
  cartItems,
  pincodeChanged,
  tabChange,
}: any) {

  const ImageTextBox = styled(BaseBox)`
    position: absolute;
    top: 5%;
    left: 5%;

    p{
      margin-top: 4px;
      margin-bottom: 4px;
      color: ${colors.primaryColor};
    }
`;

  const [showUnavailableTests, setShowUnavailableTests] = useState(false);
  const [showGatewayPe, setShowGatewayPe] = useState<any>(false);
  const [isCodeCopied, setIsCodeCopied] = useState<any>(false);
  const { utm_source } = queryString.parse(location.search) || '-';
  const unavailableTestsCount = cartItems['investigations']?.filter(x => x['available'] === false).length;
  const nonPathologyTestsCount = cartItems['investigations']?.filter(x => x['type'] !== 'Pathology').length;
  const { user } = useSelector(state => state.auth);

  useEffect(() => {
    if (searchInput === pincodeChanged && !tabChange) {
      cleverTapAnl.event({
        event_name: 'pe_ordering_service_view',
        event: {
          page_name: 'pe_ordering_landing_page',
          source: utm_source || '-',
          doctor_id: doctorID,
          visit_id: visitID,
          patient_id: patientID,
          pincode_verified_status: cartItems['investigations']?.length - unavailableTestsCount > 0 ? true : false,
          pincode_update: pincodeUpdated,
          service_for: state.isComboCard ? 'both' : 'lab',
          cart_status: unavailableTestsCount === nonPathologyTestsCount ? 'Resolved' : 'Unresolved',
          investigation_total: cartItems['investigations']?.length,
          investigation_unavailable: unavailableTestsCount,
          investigation_available: cartItems['investigations']?.length - unavailableTestsCount,
          investigation_nonpathology: nonPathologyTestsCount,
        },
      } as EventPEOrderingServiceView);
    }
  }, []);

  function handleCall() {
    cleverTapAnl.event({
      event_name: 'pe_ordering_service',
      event: {
        page_name: 'pe_ordering_landing_page',
        source: utm_source || '-',
        doctor_id: doctorID,
        visit_id: visitID,
        patient_id: patientID,
        coupon_code_flag: isCodeCopied,
        order_request_from: 'phone',
        pincode_verified_status: cartItems['investigations']?.length - unavailableTestsCount > 0 ? true : false,
        pincode_update: pincodeUpdated,
        service_for: 'lab',
        cart_status: unavailableTestsCount === nonPathologyTestsCount ? 'Resolved' : 'Unresolved',
        investigation_total: cartItems['investigations']?.length,
        investigation_unavailable: unavailableTestsCount,
        investigation_available: cartItems['investigations']?.length - unavailableTestsCount,
        investigation_nonpathology: nonPathologyTestsCount,
        investigation_status: unavailableTestsCount === 0 ? 'Resolved' : 'Unresolved',
      },
    } as EventPEOrderingService);
    window.location.href = `tel: ${state.phoneContact}`;
  }

  function handleWhatsappCall() {
    cleverTapAnl.event({
      event_name: 'pe_ordering_service',
      event: {
        page_name: 'pe_ordering_landing_page',
        source: utm_source || '-',
        doctor_id: doctorID,
        visit_id: visitID,
        patient_id: patientID,
        coupon_code_flag: isCodeCopied,
        order_request_from: 'whatsapp',
        pincode_verified_status: cartItems['investigations']?.length - unavailableTestsCount > 0 ? true : false,
        pincode_update: pincodeUpdated,
        service_for: 'lab',
        cart_status: unavailableTestsCount === nonPathologyTestsCount ? 'Resolved' : 'Unresolved',
        investigation_total: cartItems['investigations']?.length,
        investigation_unavailable: unavailableTestsCount,
        investigation_available: cartItems['investigations']?.length - unavailableTestsCount,
        investigation_nonpathology: nonPathologyTestsCount,
        investigation_status: unavailableTestsCount === 0 ? 'Resolved' : 'Unresolved',
      },
    } as EventPEOrderingService);

    let tests = '';
    cartItems['investigations']?.forEach((ele, index) => {
      tests += ele['name'];
      if (index !== cartItems['investigations']?.length - 1) {
        tests += ', ';
      }
    });

    window.location.href = `https://wa.me/${state?.whatsAppContact}?text=Name: ${user?.firstName} ${user?.lastName}%0aPhone Number: ${user?.mobileNumber}%0aTests: ${tests}`;
  }

  function showUnavailableTest() {
    setShowUnavailableTests(!showUnavailableTests);
  }

  function handleCopyText() {
    setIsCodeCopied(true);
    navigator.clipboard.writeText(state.discountCode || '');
    message.success(`'${state?.discountCode || ''}' code copied!  Paste it in 'Apply Coupon' section`, { autoClose: 2000 });
  }

  function handleSubmit() {
    cleverTapAnl.event({
      event_name: 'pe_ordering_service',
      event: {
        page_name: 'pe_ordering_landing_page',
        source: utm_source || '-',
        doctor_id: doctorID,
        visit_id: visitID,
        patient_id: patientID,
        coupon_code_flag: isCodeCopied,
        order_request_from: 'Order Lab Tests',
        pincode_verified_status: cartItems['investigations']?.length - unavailableTestsCount > 0 ? true : false,
        pincode_update: pincodeUpdated,
        service_for: 'lab',
        cart_status: unavailableTestsCount === nonPathologyTestsCount ? 'Resolved' : 'Unresolved',
        investigation_total: cartItems['investigations']?.length,
        investigation_unavailable: unavailableTestsCount,
        investigation_available: cartItems['investigations']?.length - unavailableTestsCount,
        investigation_nonpathology: nonPathologyTestsCount,
        investigation_status: unavailableTestsCount === 0 ? 'Resolved' : 'Unresolved',
      },
    } as EventPEOrderingService);

    setShowGatewayPe(true);
  }

  function handleCloseGatewayPe() {
    setShowGatewayPe(false);
  }

  if (showGatewayPe)
    return (
      <Modal
        centered
        hideFooter
        isOpen
      >
        <GatewayPeLab
          visitID={visitID}
          pincode={parseInt(searchInput)}
          onClose={handleCloseGatewayPe}
        />
      </Modal>
    );

  return (
    <Root>
      <ImageBox>
        <BackgroundImage src={state.bgImage} />
        <ImageTextBox>
          <Heading4 fontWeight={500} title={state.title} />
          <p>{state.subTitle}</p>
          <p>Use code {state.discountCode}</p>
          <FlexBox>
            <Image mr='6px' src={certifiedTick} />
            <Heading4 mt='4px'
              title={'Certified Labs'}
              color={`${colors.blueColor[0]}`}
            />
          </FlexBox>
        </ImageTextBox>
      </ImageBox>
      {
        nonPathologyTestsCount > 0 && cartItems['investigations']?.length - unavailableTestsCount !== 0 ?
          <Box>
            <Heading4 title={`${nonPathologyTestsCount} ${nonPathologyTestsCount === 1 ? 'test' : 'tests'} excluded since Pharmeasy provides only pathology test services`} />
          </Box> : null
      }
      {
        cartItems['investigations']?.map((lab: any) => (
          <>
            {
              lab['available'] ?
                <MedicineRow ml='12px' key={lab}>
                  <BaseBox>
                    <CircleWithTick />
                  </BaseBox>
                  <Heading4 title={lab['name']} />
                </MedicineRow>
                :
                null
            }
          </>
        ))
      }
      {
        unavailableTestsCount > 0 ?
          <MedicineRow ml='12px'>
            <BaseBox>
              <CloseIcon />
            </BaseBox>
            <Heading4
              title={`${unavailableTestsCount} ${unavailableTestsCount === 1 ? 'Test' : 'Tests'} unavailable`}
            />
            <HelpBox>
              <HelpIcon onClick={showUnavailableTest} />
            </HelpBox>
          </MedicineRow> : null
      }
      {
        showUnavailableTests ?
          cartItems['investigations']?.map((lab: any) => (
            <LabRow key={lab}>
              {
                !lab['available'] ?
                  <FlexBox p='0px 8px' style={{ 'flex-wrap': 'nowrap' }}>
                    <BaseBox>
                      <Bullet />
                    </BaseBox>
                    <Heading4
                      title={lab['name']}
                    />
                  </FlexBox>
                  :
                  null
              }
            </LabRow>
          ))
          : null
      }
      {
        showUnavailableTests && cartItems['investigations']?.length - unavailableTestsCount !== 0 ?
          <Box style={{ 'backgroundColor': `${colors.redColor[4]}` }}>
            <Heading4 title={' Call to confirm the availablity and serviceability of these tests!'} />
          </Box> : null
      }
      {
        cartItems['investigations']?.length - unavailableTestsCount === 0 ?
          <Box>
            <Heading4 title={'Tests aren’t serviceable at this pincode. Change pincode and try again!'} />
          </Box> : null
      }
      <Footer hidden={cartItems['investigations']?.length - unavailableTestsCount === 0 || cartItems['disableOrderCTA']}>
        <CopyBox>
          <Heading4
            p='10px 8px'
            title={state?.discountCode}
          />
          <Image
            src={copyCodeImg}
            onClick={handleCopyText}
          />
        </CopyBox>
        <PrimaryButton
          title={state.textPlaceOrderCTA}
          onClick={handleSubmit}
        />
      </Footer>
      <Footer>
        <BaseBox flex='1' ml='10px'>
          <Heading4
            fontWeight={600}
            title={'Trouble ordering online?'}
          />
          <Heading4
            title={'Order on call or whatsapp!'}
          />
        </BaseBox>
        <FlexBox>
          <SecondaryButton
            icon={<Icons.PhoneIcon width={16} />}
            onClick={handleCall}
          />
          <SecondaryButton
            icon={<WhatsAppIcon width={24} />}
            onClick={handleWhatsappCall}
          />
        </FlexBox>
      </Footer>
    </Root>
  );
}

export default LabCart;